import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Logo } from "../components/Logo";
import PoweredBy from "../components/PoweredBy";
import { Zap, Eye, EyeOff } from "lucide-react";
import api, { apiErr } from "../lib/api";

const ROLE_META = {
  manager: { label: "Manager", desc: "Full access & dashboards" },
  technician: { label: "Technician", desc: "Lab worklist" },
  parts: { label: "Parts / Spares", desc: "Spares workflow" },
  customer: { label: "Customer", desc: "Equipment portal" },
};

function Feat() { return null; }
Feat.displayName = "Feat"; // legacy export placeholder

export default function Login() {
  const { login, demoLogin } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [picked, setPicked] = useState(null);
  const [showPw, setShowPw] = useState(false);
  // Demo sign-in visibility is decided server-side (one env flag,
  // DEMO_LOGIN_ENABLED) rather than guessed from the browser's hostname —
  // that guess previously stayed silently ON for any deployment URL other
  // than the exact custom prod domain, and needed real plaintext demo
  // passwords baked into the JS bundle to work. Now no credentials ever
  // ship to the frontend at all: clicking a role just asks the backend to
  // log that demo account in directly.
  const [demoRoles, setDemoRoles] = useState([]);

  useEffect(() => {
    api.get("/auth/demo-login-info").then((r) => setDemoRoles(r.data.roles || [])).catch(() => setDemoRoles([]));
  }, []);

  const submit = async (e) => {
    e?.preventDefault();
    setBusy(true);
    setError("");
    const res = await login(email, password);
    setBusy(false);
    if (res.ok) {
      navigate("/app");
    } else {
      setError(res.error);
    }
  };

  const pickDemo = async (role) => {
    setPicked(role);
    setBusy(true);
    setError("");
    const res = await demoLogin(role);
    setBusy(false);
    if (res.ok) {
      navigate("/app");
    } else {
      setError(res.error);
    }
  };

  return (
    <div className="login-wrap">
      <div className="login-aside">
        <div className="login-glow" />
        <div style={{ display: "flex", alignItems: "center", gap: 13 }}>
          <Logo scale={1.25} dark />
        </div>
        <div>
          <div className="eyebrow">Calibration Management System</div>
          <div className="login-tag head">Precision, <span className="gold-text">Expertise &amp; Commitment.</span></div>
          <p style={{
            fontSize: 15,
            lineHeight: 1.65,
            color: "#d9d7cf",
            maxWidth: 460,
            marginTop: 18,
          }} data-testid="login-statement">
            We believe that all equipment should be quality assured to create a safe working environment,
            while guaranteeing customer satisfaction and quick turnaround time.
          </p>
        </div>
        <div className="login-foot">
          <span>PTE Connect · since 1994</span><span>•</span><span>Kempton Park, South Africa</span>
        </div>
      </div>

      <div className="login-card-wrap">
        <form className="login-form" onSubmit={submit}>
          <h2>Sign in</h2>
          <p className="sub">{demoRoles.length ? "Welcome back. Sign in or pick a demo role to explore the system." : "Welcome back. Please sign in to continue."}</p>

          <div className="field">
            <label>Email address</label>
            <input className="input" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" autoComplete="username" />
          </div>
          <div className="field">
            <label>Password</label>
            <div style={{ position: "relative" }}>
              <input className="input" style={{ paddingRight: 38 }} type={showPw ? "text" : "password"} value={password}
                onChange={(e) => setPassword(e.target.value)} data-testid="login-password" autoComplete="current-password" />
              <button type="button" onClick={() => setShowPw((v) => !v)} data-testid="login-password-toggle"
                title={showPw ? "Hide password" : "Show password"}
                style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
                         background: "none", border: 0, cursor: "pointer", color: "var(--text-3)", display: "flex" }}>
                {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          {error && <div style={{ color: "var(--red)", fontSize: 13, marginBottom: 10 }} data-testid="login-error">{error}</div>}

          {demoRoles.length > 0 && (
            <>
              <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-2)", margin: "18px 0 0" }}>Quick demo sign-in as…</div>
              <div className="role-pick">
                {demoRoles.map((role) => (
                  <button key={role} type="button" className={`role-btn ${picked === role ? "sel" : ""}`}
                    onClick={() => pickDemo(role)} disabled={busy} data-testid={`demo-${role}`}>
                    <span className="rt">{ROLE_META[role]?.label || role}</span>
                    <span className="rd">{ROLE_META[role]?.desc || ""}</span>
                  </button>
                ))}
              </div>
            </>
          )}

          <div style={{ marginTop: 14 }}>
            <button className="btn-primary" type="submit" disabled={busy} data-testid="login-submit">
              {busy ? "Signing in…" : (<>Connect <Zap size={16} style={{ display: "inline", verticalAlign: "-3px", marginLeft: 4 }} fill="currentColor" /></>)}
            </button>
          </div>
          <p style={{ fontSize: 11.5, color: "var(--text-3)", marginTop: 16, textAlign: "center" }}>PTE Connect · powered by Beyond Measure</p>
        </form>
      </div>
      <PoweredBy />
    </div>
  );
}
